import { Image } from '../../../../modules/design-system';
import type { TierBadge } from '../../../../modules/api';
import { TierSelector } from '../../../classic/Pack/PackInfoCard/TierSelector';

export interface PackInfoCardProps {
  title: string;
  buybackPercent?: number;
  /**
   * In the modern template these are the sibling packs in the active
   * category, not the offer's own suitable_badges — each card represents
   * one slide of the slider.
   */
  tiers: TierBadge[];
  /**
   * Pack logo banner — moved to the sidebar on desktop layouts (Figma
   * `543:2118`). Mobile keeps it on the pedestal inside the slider.
   * `null` falls back to the title-only header.
   */
  packLogoImageUrl?: string | null;
  /**
   * Fires with the clicked offer id. In the modern template the parent
   * wires this to `onSlideChange`, so a tier click focuses the slider
   * on that pack — no URL navigation, no page reload (unlike classic).
   */
  onTierSelect?: (poolOfferId: number) => void;
}

/**
 * Modern PackInfoCard — Figma `543:2118` (desktop sidebar header).
 *
 * Anatomy (top → bottom):
 *   1. **Logo banner** — the offer's `pack_logo_image_url` rendered big
 *      at the card's top (desktop only). Falls back to the title
 *      heading when the offer has no logo override or on mobile.
 *   2. **Subtitle** — `{buybackPercent}% FMV buyback available — or hold
 *      and redeem your vaulted graded card.` (bold percent).
 *   3. **Tier-card row** — one card per sibling pack in the active
 *      category. Active card mirrors the slider's current pack. Clicking
 *      a card focuses the slider on that pack (no URL update).
 */
export function PackInfoCard({
  title,
  buybackPercent = 90,
  tiers,
  packLogoImageUrl = null,
  onTierSelect,
}: PackInfoCardProps) {
  return (
    // No `overflow-hidden` here on desktop so the pack logo can bleed
    // above the card edge (Figma 543:2118 — logo's bottom overlaps the
    // card's top while the logo top floats outside). On mobile / tablet
    // the layout still needs clipping for the rounded-corner gradient,
    // but no logo is rendered there so it doesn't matter.
    //
    // When the logo is present we also push the *card* itself down by
    // the same amount the logo peeks up (56 px). Otherwise the logo
    // would crash into the sticky header above. With no logo
    // (`title-only` fallback) the card stays at its natural grid
    // position so the rest of the sidebar starts as high as possible.
    // The whole PackInfoCard is desktop-only — Figma mobile/tablet
    // shows tier info on the slider's pedestal-logo instead and uses
    // the BottomAction strip for buyback / price info. Mobile users
    // never see this card at all.
    <div
      data-co-component="PackInfoCard"
      className={
        'hidden min-[900px]:block ' +
        'relative [border-radius:var(--co-PackInfoCard-borderRadius,28px)] ' +
        'shadow-[0_64px_64px_0_rgba(0,0,0,0.05)] text-white ' +
        'px-[14px] py-5 ' +
        (packLogoImageUrl ? 'min-[900px]:pt-0 min-[900px]:mt-[56px]' : '')
      }
      // Single operator-driven gradient. `color-mix` applies the bgOpacity
      // knob to each stop so the partner can keep glass blur visible (low
      // opacity) or push to a solid fill (opacity = 1). Default bgFrom/bgTo
      // carry alpha=0.20 reproducing the old glass wash out of the box.
      style={{
        backgroundImage:
          'linear-gradient(' +
          'var(--co-PackInfoCard-bgDirection, to bottom), ' +
          'color-mix(in srgb, var(--co-PackInfoCard-bgFrom, rgba(255,255,255,0.20)) ' +
          'calc(var(--co-PackInfoCard-bgOpacity, 1) * 100%), transparent) 0%, ' +
          'color-mix(in srgb, var(--co-PackInfoCard-bgTo, rgba(153,153,153,0.20)) ' +
          'calc(var(--co-PackInfoCard-bgOpacity, 1) * 100%), transparent) 100%)',
        backdropFilter: 'blur(var(--co-PackInfoCard-glassBlur, 27px))',
        WebkitBackdropFilter: 'blur(var(--co-PackInfoCard-glassBlur, 27px))',
        borderStyle: 'solid',
        borderWidth: 'var(--co-PackInfoCard-borderWidth, 2px)',
        borderColor: 'var(--co-PackInfoCard-borderColor, rgba(171,174,179,0.24))',
      }}
    >
      <div className="flex flex-col items-center gap-3 text-center text-white">
        {packLogoImageUrl && (
          // Logo "peeks" out of the card: pulled up by 56px via a
          // negative top-margin so the lower third still sits on the
          // card surface, matching Figma 543:2118. Hidden below desktop.
          <div className="hidden min-[900px]:block relative w-[78%] aspect-[233/117] -mt-[56px] [filter:drop-shadow(0_18px_28px_rgba(0,0,0,0.45))]">
            <Image
              src={packLogoImageUrl}
              alt={title}
              className="absolute inset-0"
              imgClassName="absolute inset-0 h-full w-full object-contain"
            />
          </div>
        )}

        <h2
          className={
            'm-0 text-center uppercase font-bold ' +
            'leading-[28px] tracking-[-0.3px] ' +
            (packLogoImageUrl ? 'min-[900px]:hidden' : '')
          }
          style={{
            color: 'var(--co-PackInfoCard-titleColor, #ffffff)',
            fontFamily: 'var(--co-PackInfoCard-fontFamily), Inter, system-ui, sans-serif',
            fontSize: 'var(--co-PackInfoCard-titleSize, 24px)',
          }}
        >
          {title || 'Pack title'}
        </h2>

        <p
          className="m-0 max-w-[309px] text-center leading-[20px] tracking-[-0.1px] font-normal"
          style={{
            color: 'var(--co-PackInfoCard-subtitleColor, rgba(255,255,255,0.8))',
            fontFamily: 'var(--co-PackInfoCard-fontFamily), Inter, system-ui, sans-serif',
            fontSize: 'var(--co-PackInfoCard-subtitleSize, 13px)',
          }}
        >
          <span className="font-semibold">{buybackPercent}% FMV buyback</span>
          {' '}available — or hold and redeem your vaulted graded card.
        </p>
      </div>

      {tiers.length > 0 && (
        <div className="pt-4">
          <TierSelector
            badges={tiers}
            {...(onTierSelect ? { onSelect: onTierSelect } : {})}
          />
        </div>
      )}
    </div>
  );
}
